import { create } from 'zustand'
import { HookResult } from '../hooks/hookTypes'
import axiosInstance, { axiosRequest } from '../network/axiosInstance'

// Define the initial state for the store
const initialState: HookResult = {
  loading: false,
  success: false,
  error: false,
  data: null,
  errorData: null,
  execute: () => { },
}

export interface Project {
  id: string
  projectName: string
  createdAt: string
  docName: string
  systemAgentEnabled: boolean | null
  projectDescription?: string
  projectType?: 'personal' | 'collaboration'
  startingAngle?: 'interview' | 'survey' | 'quickstart' | 'smartnotes' | 'chat' | 'research' | 'blank'
  passwordProtected?: boolean
  isInitialized?: boolean
  nodes?: any[]
  edges?: any[]
  organizationId?: string
  organization?: {
    id: string
    name: string
  }
  // Add other project-specific fields as needed
}

export interface ProjectsState {
  projectsLoading: boolean
  projectsFetchSuccess: boolean
  projectsFetchError: boolean
  projects: Project[] | null
  projectsErrorMessage: string | null
  fetchProjects: () => Promise<void>
}

// Create a Zustand store for fetching projects
export const useGetProjects = create<ProjectsState>((set) => ({
  projectsLoading: false,
  projectsFetchSuccess: false,
  projectsFetchError: false,
  projects: null,
  projectsErrorMessage: null,
  fetchProjects: async () => {
    console.log('fetchProjects called')
    set({
      projectsLoading: true,
      projectsFetchSuccess: false,
      projectsFetchError: false,
      projects: null,
      projectsErrorMessage: null,
    })
    try {
      const res = await axiosInstance.get('/api/projects')
      set({
        projectsLoading: false,
        projectsFetchSuccess: true,
        projects: res.data.projects,
      })
    } catch (err) {
      console.error('Error fetching projects:', err)
      set({
        projectsLoading: false,
        projectsFetchError: true,
        projectsErrorMessage: (err as Error).message,
      })
    }
  },
}))

// Create new project
export const useCreateProject = axiosRequest({
  method: 'POST',
  url: '/api/projects',
})

// Get project by ID
export const useGetProject = axiosRequest({
  method: 'GET',
  url: '/api/projects/:id',
})

// Update project
export const useUpdateProject = axiosRequest({
  method: 'PUT',
  url: '/api/projects/:id',
})

// Update project system agent enabled setting
export const useUpdateProjectSystemAgentEnabled = axiosRequest({
  method: 'PUT',
  url: '/api/projects/:id/systemAgentEnabled',
})

// Delete project
export const useDeleteProject = axiosRequest({
  method: 'DELETE',
  url: '/api/projects/:id',
})

// Add new access to project
export const useGetProjectAccessRules = axiosRequest({
  method: 'GET',
  url: '/api/projects/access/:id',
})

// Add new access to project
export const useAddAccessToProject = axiosRequest({
  method: 'POST',
  url: '/api/projects/access/:id',
})

// Update project access
export const useUpdateAccessToProject = axiosRequest({
  method: 'PUT',
  url: '/api/projects/access/:id',
})

// Delete access from project
export const useDeleteAccessToProject = axiosRequest({
  method: 'DELETE',
  url: '/api/projects/access/:id',
})

// Update project name and description
export const useUpdateProjectDetails = axiosRequest({
  method: 'PUT',
  url: '/api/projects/:id/details',
})

// Mark project as initialized
export const useMarkProjectInitialized = axiosRequest({
  method: 'PUT',
  url: '/api/projects/:projectId/initialize',
})

/* Projects data format
"projects": [
    {
        "id": "66d56c7c8a7ab0bad26f9a5e",
        "createdByUserId": "66210d28fd1690725869ffb0",
        "projectName": "Error esting",
        "createdAt": "2024-09-02T07:42:52.469Z",
        "updatedAt": "2024-09-04T00:46:55.588Z",
        "isArchived": false,
        "lastUpdatedByUserId": "66210d28fd1690725869ffb0",
        "nodes": [
            {
                "id": "SmartNotesComponent",
                "type": "basenode",
                "position": {
                    "x": 2688,
                    "y": -1440
                },
                "data": {
                    "label": "Prismaheader",
                    "showHeader": false,
                    "nodetype": "SmartNotesNodeComponent",
                    "content": {
                        "compare": null
                    },
                    "title": "Initial Smart Notes",
                    "position": {
                        "x": 2688,
                        "y": -1440
                    },
                    "externalData": {
                        "dataEdge": []
                    },
                    "notesInput": "dfefwefwefwefwewfqwefqwrgqertghqergqwergqwrgqwrqwrggqwerg\n\n\nqwrgqwrgqwrgqwrg\n\nqwrg\nqg\nqg\nrg\nqwrgqwrqwrgqwgrqwrgqwrgqwergqefqefefqeqefqefq\n\n\neqefefqeldflfolfolflfergergergergfvsdfvsvfdfssdfgsdvsdvsdvwewevwsevwevevweefqefqefqeffvwev",
                    "editor": false,
                    "tags": [],
                    "lastUpdate": 1725266555447
                },
                "dragHandle": ".custom-drag-handle",
                "selected": false,
                "measured": {
                    "width": 1404,
                    "height": 1004
                },
                "dragging": false
            },
        ],
        "edges": [],
        "projectDescription": "First blood edition",
        "status": "Uninitialized project",
        "systemAgentEnabled": null, 
        "settings": [
            {
                "id": "66d56c7c8a7ab0bad26f9a5f",
                "projectId": "66d56c7c8a7ab0bad26f9a5e",
                "accessRules": [
                    {
                        "id": "66d56c7c8a7ab0bad26f9a60",
                        "settingsId": "66d56c7c8a7ab0bad26f9a5f",
                        "userId": "66210d28fd1690725869ffb0",
                        "accessLevel": "full"
                    }
                ]
            }
        ]
    }
]
*/
